// Componente Privacidad
// Página de políticas de privacidad de Amarsi

import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import Button from '../../components/UI/Button';
import AmarsiLogo from '../../components/Icons/AmarsiLogo';

/**
 * Componente Privacidad
 * 
 * Muestra las políticas de privacidad de Amarsi
 * Accessible tanto para usuarios autenticados como públicos
 */
const Privacidad = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900">
      {/* Header */}
      <header className="bg-white dark:bg-neutral-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/" className="flex items-center text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
                <FiArrowLeft className="h-5 w-5 mr-2" />
                Volver al inicio
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <AmarsiLogo width='2rem' height='2rem'/>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Amarsi</h1>
            </div>
            <nav className="flex items-center space-x-4">
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/terminos-compras" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Términos de Compra
              </Link>
              <Link to="/login">
                <Button variant="primary" size="sm">
                  Iniciar Sesión
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Contenido principal */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Política de Privacidad <small className="text-lg font-normal"> Actualizado el 2026-03-25</small>
            </h1>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Amarsi. ("nosotros", "nuestro" o "nos") se compromete a proteger su privacidad. 
              Esta Política de privacidad explica cómo recopilamos, usamos y protegemos su información personal.
            </p>
          </div>

          <div className="space-y-8 text-gray-700 dark:text-gray-300">
            <section>
              <p>
                Esta Política de privacidad se aplica a nuestro sitio web, https://clubamarsi.com, y sus subdominios asociados 
                (colectivamente, nuestro "Servicio") junto con nuestra aplicación, Amarsi.
              </p>
              <br />
              <p>
                Al acceder o utilizar nuestro Servicio, usted indica que ha leído, comprendido y acepta nuestra recopilación, 
                almacenamiento, uso y divulgación de su información personal como se describe en esta Política de privacidad 
                y en nuestros Términos de servicio.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Recopilación y Uso de Información</h2>
              
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">1.1 Información sobre la Ubicación en Segundo Plano y Funcionalidad "Radar"</h3>
              <p>
                Nuestra aplicación utiliza datos de ubicación en segundo plano para proporcionar la funcionalidad principal llamada "Radar". 
                Esta función permite a nuestros usuarios sociabilizar con personas cercanas a su ubicación en tiempo real. 
                La información sobre la ubicación en segundo plano se utiliza exclusivamente para ofrecer esta experiencia social 
                y mejorar la interacción entre nuestros usuarios.
              </p>

              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">1.2 Consentimiento del Usuario</h3>
              <p>
                Al utilizar nuestra aplicación, usted acepta el uso de la información sobre la ubicación en segundo plano para la 
                funcionalidad "Radar", tal como se describe en esta política de privacidad. Si no está de acuerdo con esta 
                recopilación y uso, le recomendamos que no utilice nuestra aplicación.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Administración de Permisos</h2>
              
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">2.1 Solicitud de Permisos</h3>
              <p>
                Para habilitar la funcionalidad "Radar" y acceder a la información sobre la ubicación en segundo plano, 
                nuestra aplicación solicitará su permiso explícito. Antes de solicitar este permiso, proporcionaremos una 
                explicación clara y comprensible del propósito de esta recopilación y cómo beneficiará su experiencia con la aplicación.
              </p>

              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">2.2 Revocación del Permiso</h3>
              <p>
                Usted tiene el derecho de revocar su permiso en cualquier momento. Si decide revocar su permiso para acceder a 
                la información sobre la ubicación en segundo plano, puede hacerlo a través de la configuración de su dispositivo. 
                Tenga en cuenta que, al revocar este permiso, la funcionalidad "Radar" y la capacidad de sociabilizar con personas 
                cercanas a su ubicación pueden verse afectadas.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. Información de Geolocalización</h2>
              <p>
                Si le ha dado a Amarsi acceso a tu ubicación en la configuración de tu dispositivo, cuando uses tu dispositivo móvil, 
                recopilaremos información de ubicación sobre tu longitud y latitud, y podemos guardar las coordenadas de tu dispositivo 
                para ofrecerte ciertas funciones. Esta información nos ayuda a identificar tu ubicación física y la usamos para 
                personalizar la App y facilitarte la interacción con otros Usuarios.
              </p>
              
              <p className="mt-4">
                Si ha dado acceso a Amarsi a los servicios de ubicación pero desea deshabilitarlos, puede hacerlo siguiendo uno de los siguientes métodos:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Aplicación para iPhone: Ajustes → Privacidad, Servicios de ubicación → Amarsi</li>
                <li>Aplicación para Android: Ajustes → Ubicación → Amarsi → Permisos → Ubicación</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">4. Seguridad y Protección de Datos</h2>
              <p>
                Nos comprometemos a proteger su información personal y garantizar su seguridad. Implementamos medidas de seguridad 
                adecuadas para evitar el acceso no autorizado, la divulgación o la alteración de su información. Además, nos aseguramos 
                de que cualquier tercero con el que compartamos su información también mantenga altos estándares de seguridad y protección de datos.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">5. Divulgación a Terceros</h2>
              <p>
                No compartiremos su información sobre la ubicación en segundo plano con terceros sin su consentimiento explícito, 
                excepto en los siguientes casos:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Cuando sea necesario para cumplir con la ley o con una orden judicial.</li>
                <li>Para proteger nuestros derechos, privacidad, seguridad o propiedad, o los de nuestros usuarios u otros.</li>
                <li>En caso de una fusión, adquisición o venta de la totalidad o parte de nuestros activos, siempre y cuando el tercero se comprometa a cumplir con esta política de privacidad.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">6. Información que Recopilamos</h2>
              <p>Recopilamos información suya cuando visita nuestra plataforma, se registra en nuestro sitio, realiza un pedido, se suscribe a nuestro boletín, responde a una encuesta o completa un formulario.</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Información Personal:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Nombre / nombre de usuario</li>
                  <li>Números de teléfono</li>
                  <li>Correos electrónicos</li>
                  <li>Direcciones de correo</li>
                  <li>Direcciones de facturación</li>
                  <li>Edad</li>
                  <li>Contraseña</li>
                </ul>
              </div>

              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Información de Dispositivo Móvil (Opcional):</h3>
                <p>También recopilamos información de dispositivos móviles para una mejor experiencia de usuario, aunque estas funciones son completamente opcionales:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Cámara (imágenes): otorgar permiso a la cámara le permite al usuario cargar cualquier imagen directamente desde la plataforma</li>
                  <li>Galería de fotos (imágenes): Otorgar acceso a la galería de fotos permite al usuario cargar cualquier imagen de su galería</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">7. Contacto</h2>
              <p>
                Si tiene preguntas o inquietudes sobre esta Política de Privacidad, no dude en contactarnos:
              </p>
              <div className="mt-4 bg-violet-50 dark:bg-violet-900/20 p-4 rounded-lg border border-violet-200 dark:border-violet-700">
                <p className="text-violet-800 dark:text-violet-200">
                  Correo electrónico:{' '}
                  <a href="mailto:hola@clubamarsi.com" className="font-semibold underline">
                    hola@clubamarsi.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-neutral-800 mt-4 border-t border-gray-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <AmarsiLogo width='2rem' height='2rem'/>
              <span className="text-gray-900 dark:text-white font-semibold">Amarsi</span>
              <span className="text-gray-500">© {new Date().getFullYear()} Amarsi. Todos los derechos reservados.</span>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-white font-medium"
              >
                Privacidad
              </Link>
              <Link 
                to="/terminos-compras" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Términos de Compra
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacidad;
